import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthProvider';
import { API_BASE } from './api';
import Avatar from './Avatar';
import { uploadImage } from './uploadImage';

// Your profile: the username is fixed; the picture can be added, changed or
// removed. It shows next to the frog in the footer.
export default function ProfilePage() {
  const { user, token, updateUser } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Pick up a picture changed on another device.
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/account/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((profile) => {
        if (profile && profile.avatarUrl !== (user?.avatarUrl || '')) updateUser({ avatarUrl: profile.avatarUrl });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const savePicture = async (avatarUrl) => {
    const res = await fetch(`${API_BASE}/api/account/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ avatarUrl }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Couldn’t save your picture.');
    updateUser({ avatarUrl: data.avatarUrl });
  };

  const onFileChosen = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await savePicture(await uploadImage(file));
      setMessage('Picture saved.');
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setBusy(false);
    }
  };

  const removePicture = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await savePicture('');
      setMessage('Picture removed.');
    } catch (removeError) {
      setError(removeError.message);
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="contact-page">
      <div className="contact-card profile-card">
        <h1>Your profile</h1>

        <div className="profile-picture">
          <Avatar user={user} size={112} className="profile-avatar" />
          <div className="profile-picture-actions">
            <button type="button" className="profile-button is-primary" onClick={() => fileRef.current.click()} disabled={busy}>
              {busy ? 'Saving…' : user.avatarUrl ? 'Change picture' : 'Upload picture'}
            </button>
            {user.avatarUrl && (
              <button type="button" className="profile-button" onClick={removePicture} disabled={busy}>
                Remove picture
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFileChosen}
              className="banner-picker-input"
              tabIndex={-1}
              aria-hidden="true"
            />
          </div>
        </div>

        {error && <p className="profile-note is-error" role="alert">{error}</p>}
        {message && <p className="profile-note" role="status">{message}</p>}

        <label className="profile-field">
          <span>Username</span>
          <input type="text" value={user.username || user.name || ''} readOnly aria-readonly="true" />
        </label>
        <p className="profile-hint">Usernames can’t be changed.</p>
      </div>
    </div>
  );
}
